import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { DiagramDocument } from "@/types/diagram";

// Per-window diagram bodies, keyed by diagramDocId (TODO §21/§4.3.1). A mirror of
// `griddSlice` and for the same reason: the editor emits a full scene on a ~400ms debounce
// while someone is drawing, and folding that into `DeckDocument` would rewrite the whole
// deck per stroke.
export type DiagramState = Record<string, DiagramDocument>;

const initialState: DiagramState = {};

const diagramSlice = createSlice({
  name: "diagram",
  initialState,
  reducers: {
    // §17.4.5 MERGES a map in, rather than replacing it — the nugget path's one new
    // reducer. Inserting a nugget brings its diagram scenes into a session that is already
    // holding this deck's, so `hydrated` (which replaces) would wipe the deck.
    //
    // It is deliberately NOT in the persistence listener's matcher: these bodies were just
    // READ out of IndexedDB under the nugget's owner key, and writing them straight back —
    // under this deck's owner, which is what a naive listener would do — is the exact fork
    // §17.1.5 exists to prevent.
    diagramDocsMerged(state, action: PayloadAction<DiagramState>) {
      Object.assign(state, action.payload);
    },
    // Replaces the whole map on IndexedDB hydration.
    hydrated(_state, action: PayloadAction<DiagramState>) {
      return action.payload;
    },
    diagramDocCreated(
      state,
      action: PayloadAction<{ docId: string; doc: DiagramDocument }>,
    ) {
      state[action.payload.docId] = action.payload.doc;
    },
    diagramDocUpdated(
      state,
      action: PayloadAction<{ docId: string; doc: DiagramDocument }>,
    ) {
      state[action.payload.docId] = action.payload.doc;
    },
    // Unlinks the body from this session. Whether the IndexedDB row is actually DELETED is
    // a separate question, answered in the persistence listener against the row's owner key
    // (§4.3.4 / §17.6.2): a nugget-owned body is shared, so this deck removing the slide
    // must not destroy it for the other four decks using it.
    diagramDocRemoved(state, action: PayloadAction<{ docId: string }>) {
      delete state[action.payload.docId];
    },
  },
});

export const {
  hydrated,
  diagramDocsMerged,
  diagramDocCreated,
  diagramDocUpdated,
  diagramDocRemoved,
} = diagramSlice.actions;

export default diagramSlice.reducer;
